import ReactReconciler from "react-reconciler";
import {
  ConcurrentRoot,
  LegacyRoot,
  DefaultEventPriority,
} from "react-reconciler/constants.js";
import {
  MUTATION_TYPE_INSERT_CHILD,
  MUTATION_TYPE_REMOVE_CHILD,
  MUTATION_TYPE_UPDATE_TEXT,
  MUTATION_TYPE_UPDATE_PROPERTY,
  NODE_TYPE_ELEMENT,
  NODE_TYPE_TEXT,
  ROOT_ID,
  type RemoteConnection,
  type RemoteNodeSerialization,
} from "@remote-dom/core";

// ── Internal node representation ───────────────────────────────────
//
// Lightweight JS-object stand-ins for the Remote DOM tree. Each node carries
// a stable string id that the host uses to address mutations. We do NOT use
// `@remote-dom/core`'s `RemoteElement` Custom Element classes because:
//   1. They require `customElements` registration which the worker realm
//      does not have by default (would force every plugin to bundle a DOM
//      polyfill).
//   2. The plain-object form serialises identically over the connection's
//      `RemoteNodeSerialization` contract — the host's `RemoteReceiver`
//      reconstructs proper elements on its side.

interface RemoteElementInstance {
  readonly id: string;
  readonly kind: "element";
  readonly type: string;
  properties: Record<string, unknown>;
  eventListeners: Record<string, (...args: unknown[]) => unknown>;
  children: RemoteNode[];
  parent: RemoteParent | null;
}

interface RemoteTextInstance {
  readonly id: string;
  readonly kind: "text";
  text: string;
  parent: RemoteParent | null;
}

interface RemoteRootContainer {
  readonly id: typeof ROOT_ID;
  readonly kind: "root";
  children: RemoteNode[];
  readonly connection: RemoteConnection;
}

type RemoteNode = RemoteElementInstance | RemoteTextInstance;
type RemoteParent = RemoteElementInstance | RemoteRootContainer;

let nextId = 0;
function newId(): string {
  nextId += 1;
  return `r-${nextId.toString(36)}`;
}

function serialize(node: RemoteNode): RemoteNodeSerialization {
  if (node.kind === "text") {
    return { id: node.id, type: NODE_TYPE_TEXT, data: node.text };
  }
  // Event-listener functions are intentionally NOT included in the
  // serialised payload — they can't be structured-cloned across a
  // MessagePort and would throw `DataCloneError` on the first commit.
  // Listeners are retained locally on the RemoteElementInstance for the
  // future `ethisys:remotedom:call` round-trip (see createRemoteRoot.ts's
  // top-of-file "What's NOT plumbed yet" note); until that channel lands,
  // worker-side `onClick`-style handlers are no-ops on the host. The host
  // RemoteReceiver tolerates the missing field — `eventListeners` is
  // optional on `RemoteNodeSerialization`.
  return {
    id: node.id,
    type: NODE_TYPE_ELEMENT,
    element: node.type,
    properties: node.properties,
    children: node.children.map(serialize),
  };
}

function indexInParent(parent: RemoteParent, child: RemoteNode): number {
  return parent.children.indexOf(child);
}

// ── Prop classification ────────────────────────────────────────────
//
// Children (the JSX content) are handled by the reconciler tree walk;
// `ref` and `key` are React-internal. Everything else is forwarded as a
// remote property OR an event listener. Convention: any prop starting with
// `on` followed by an uppercase letter is treated as an event listener.

function isEventListenerProp(key: string): boolean {
  return key.length > 2 && key.startsWith("on") && key[2] >= "A" && key[2] <= "Z";
}

function eventNameFromProp(propName: string): string {
  // onClick → click. The host's RemoteReceiver expects lowercase event names.
  return propName.slice(2).toLowerCase();
}

function isReservedProp(key: string): boolean {
  return key === "children" || key === "key" || key === "ref";
}

function classifyProps(props: Record<string, unknown>): {
  properties: Record<string, unknown>;
  eventListeners: Record<string, (...args: unknown[]) => unknown>;
} {
  const properties: Record<string, unknown> = {};
  const eventListeners: Record<string, (...args: unknown[]) => unknown> = {};
  for (const key of Object.keys(props)) {
    if (isReservedProp(key)) {
      continue;
    }
    const value = props[key];
    if (isEventListenerProp(key) && typeof value === "function") {
      eventListeners[eventNameFromProp(key)] = value as (...args: unknown[]) => unknown;
    } else if (value !== undefined) {
      properties[key] = value;
    }
  }
  return { properties, eventListeners };
}

// ── HostConfig factory ─────────────────────────────────────────────

/**
 * Build the react-reconciler instance bound to the supplied `RemoteConnection`.
 * Every mutation the reconciler applies translates into a single
 * `connection.mutate([record])` call; the connection forwards the record over
 * the `MessagePort` set up by `createRemoteRoot`.
 */
export function createReactReconciler(connection: RemoteConnection): {
  reconciler: ReactReconciler.Reconciler<
    RemoteParent,
    RemoteElementInstance,
    RemoteTextInstance,
    never,
    RemoteElementInstance
  >;
  createContainer: () => RemoteRootContainer;
} {
  const reconciler = ReactReconciler<
    string, // type
    Record<string, unknown>, // props
    RemoteParent, // container
    RemoteElementInstance, // instance
    RemoteTextInstance, // text instance
    never, // suspense instance
    never, // hydratable instance
    RemoteElementInstance, // public instance (returned from ref)
    object, // host context
    string[], // update payload (list of changed prop names)
    never, // child set (persistent mode only)
    ReturnType<typeof setTimeout>, // timeout handle
    -1 // no timeout sentinel
  >({
    supportsMutation: true,
    supportsPersistence: false,
    supportsHydration: false,
    isPrimaryRenderer: true,
    noTimeout: -1,
    scheduleTimeout: setTimeout,
    cancelTimeout: clearTimeout,
    getCurrentEventPriority: () => DefaultEventPriority,
    getRootHostContext: () => ({}),
    getChildHostContext: () => ({}),
    prepareForCommit: () => null,
    resetAfterCommit: () => {},
    preparePortalMount: () => {},
    shouldSetTextContent: (_type, props) =>
      typeof props.children === "string" || typeof props.children === "number",
    getPublicInstance: (instance) => instance as RemoteElementInstance,
    clearContainer: (container) => {
      // Remove every direct child from the root. The host receiver mirrors
      // each removal on its side.
      while (container.children.length > 0) {
        connection.mutate([[MUTATION_TYPE_REMOVE_CHILD, container.id, 0]]);
        container.children.shift();
      }
    },

    // ── createInstance / createTextInstance ────────────────────────
    createInstance: (type, props): RemoteElementInstance => {
      const { properties, eventListeners } = classifyProps(props);
      return {
        id: newId(),
        kind: "element",
        type,
        properties,
        eventListeners,
        children: [],
        parent: null,
      };
    },
    createTextInstance: (text): RemoteTextInstance => ({
      id: newId(),
      kind: "text",
      text,
      parent: null,
    }),

    // ── Initial mount: build the subtree before attaching to root ──
    appendInitialChild: (parent, child) => {
      parent.children.push(child);
      child.parent = parent;
    },
    finalizeInitialChildren: () => false,

    // ── Container-level mutations (root attach/detach) ─────────────
    appendChildToContainer: (container, child) => {
      const index = container.children.length;
      container.children.push(child);
      child.parent = container;
      connection.mutate([
        [MUTATION_TYPE_INSERT_CHILD, container.id, serialize(child), index],
      ]);
    },
    removeChildFromContainer: (container, child) => {
      const index = indexInParent(container, child);
      if (index < 0) {
        return;
      }
      container.children.splice(index, 1);
      child.parent = null;
      connection.mutate([[MUTATION_TYPE_REMOVE_CHILD, container.id, index]]);
    },
    insertInContainerBefore: (container, child, beforeChild) => {
      const beforeIndex = indexInParent(container, beforeChild);
      const insertAt = beforeIndex < 0 ? container.children.length : beforeIndex;
      container.children.splice(insertAt, 0, child);
      child.parent = container;
      connection.mutate([
        [MUTATION_TYPE_INSERT_CHILD, container.id, serialize(child), insertAt],
      ]);
    },

    // ── Sibling-level mutations (incremental tree updates) ─────────
    appendChild: (parent, child) => {
      const index = parent.children.length;
      parent.children.push(child);
      child.parent = parent;
      connection.mutate([
        [MUTATION_TYPE_INSERT_CHILD, parent.id, serialize(child), index],
      ]);
    },
    removeChild: (parent, child) => {
      const index = indexInParent(parent, child);
      if (index < 0) {
        return;
      }
      parent.children.splice(index, 1);
      child.parent = null;
      connection.mutate([[MUTATION_TYPE_REMOVE_CHILD, parent.id, index]]);
    },
    insertBefore: (parent, child, beforeChild) => {
      const beforeIndex = indexInParent(parent, beforeChild);
      const insertAt = beforeIndex < 0 ? parent.children.length : beforeIndex;
      parent.children.splice(insertAt, 0, child);
      child.parent = parent;
      connection.mutate([
        [MUTATION_TYPE_INSERT_CHILD, parent.id, serialize(child), insertAt],
      ]);
    },

    // ── Update path ────────────────────────────────────────────────
    prepareUpdate: (_instance, _type, oldProps, newProps): string[] | null => {
      const changed: string[] = [];
      const allKeys = new Set<string>([
        ...Object.keys(oldProps),
        ...Object.keys(newProps),
      ]);
      for (const key of allKeys) {
        if (isReservedProp(key)) {
          continue;
        }
        if (oldProps[key] !== newProps[key]) {
          changed.push(key);
        }
      }
      return changed.length > 0 ? changed : null;
    },
    commitUpdate: (instance, updatePayload, _type, _oldProps, newProps) => {
      if (updatePayload === null) {
        return;
      }
      for (const key of updatePayload) {
        const value = newProps[key];
        if (isEventListenerProp(key) && (typeof value === "function" || value === undefined)) {
          const eventName = eventNameFromProp(key);
          if (typeof value === "function") {
            instance.eventListeners[eventName] = value as (...args: unknown[]) => unknown;
          } else {
            delete instance.eventListeners[eventName];
          }
          // Functions can't cross a MessagePort via structured clone, so the
          // wire payload only signals whether the listener is now present or
          // cleared — `true` for "listener exists, wire it on the host once
          // the call channel lands", `null` for "listener removed". See the
          // serialize() comment for the broader Phase 1 limitation.
          connection.mutate([
            [
              MUTATION_TYPE_UPDATE_PROPERTY,
              instance.id,
              eventName,
              typeof value === "function" ? true : null,
              3 /* event listener */,
            ],
          ]);
        } else {
          if (value === undefined) {
            delete instance.properties[key];
          } else {
            instance.properties[key] = value;
          }
          connection.mutate([
            [MUTATION_TYPE_UPDATE_PROPERTY, instance.id, key, value],
          ]);
        }
      }
    },
    commitTextUpdate: (textInstance, _oldText, newText) => {
      textInstance.text = newText;
      connection.mutate([[MUTATION_TYPE_UPDATE_TEXT, textInstance.id, newText]]);
    },

    // ── No-op methods required by the HostConfig contract ──────────
    detachDeletedInstance: () => {},
    beforeActiveInstanceBlur: () => {},
    afterActiveInstanceBlur: () => {},
    prepareScopeUpdate: () => {},
    getInstanceFromScope: () => null,
    getInstanceFromNode: () => null,

    // Microtask scheduling — use the platform's default queueing.
    supportsMicrotasks: true,
    scheduleMicrotask:
      typeof queueMicrotask === "function"
        ? queueMicrotask
        : (callback) => Promise.resolve().then(callback),
  });

  function createContainer(): RemoteRootContainer {
    return { id: ROOT_ID, kind: "root", children: [], connection };
  }

  return { reconciler, createContainer };
}

export { ConcurrentRoot, LegacyRoot };
export type { RemoteRootContainer, RemoteElementInstance, RemoteTextInstance };
